import React, { useState, useEffect } from 'react';
import DashboardCards from '../../components/DashboardCards/DashboardCards';
import DashboardChart from '../../components/DashboardChart/DashboardChart';
import UpcomingCollections from '../../components/UpcomingCollections/UpcomingCollections';
import { getClients, getTransactions, getOtherRevenues, addOtherRevenue, getExpenses, addExpense } from '../../services/clientService';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const [clients, setClients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [otherRevenues, setOtherRevenues] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRevenueForm, setShowRevenueForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [revData, setRevData] = useState({ amount: '', type: '', date: new Date().toISOString().split('T')[0] });
  const [expData, setExpData] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [clientsData, txData, revData, expDataList] = await Promise.all([
          getClients(),
          getTransactions(),
          getOtherRevenues(),
          getExpenses()
        ]);
        const allClients = clientsData || [];
        const activeClients = allClients.filter(c => c.status !== 'inactive');
        const inactiveIds = new Set(allClients.filter(c => c.status === 'inactive').map(c => c.id));
        
        const activeTxs = (txData || []).filter(tx => !tx.clientId || !inactiveIds.has(tx.clientId));

        setClients(activeClients);
        setTransactions(activeTxs);
        setOtherRevenues(revData || []);
        setExpenses(expDataList || []);
      } catch (error) {
        console.error("Failed to load dashboard data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const groupedOtherRevenues = {};
  otherRevenues.forEach(rev => {
    const d = new Date(rev.date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    if (!groupedOtherRevenues[d]) {
      groupedOtherRevenues[d] = [];
    }
    groupedOtherRevenues[d].push(rev);
  });
  const sortedDates = Object.keys(groupedOtherRevenues).sort((a, b) => new Date(b) - new Date(a));

  const groupedExpenses = {};
  expenses.forEach(exp => {
    const d = new Date(exp.date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    if (!groupedExpenses[d]) {
      groupedExpenses[d] = [];
    }
    groupedExpenses[d].push(exp);
  });
  const sortedExpenseDates = Object.keys(groupedExpenses).sort((a, b) => new Date(b) - new Date(a));

  const handleAddOtherRevenue = async (e) => {
    e.preventDefault();
    if (!revData.amount || !revData.type) return;
    
    setIsLoading(true);
    try {
      const added = await addOtherRevenue({
        amount: parseFloat(revData.amount),
        type: revData.type,
        date: new Date(revData.date || new Date()).toISOString()
      });
      setOtherRevenues([added, ...otherRevenues]);
      setShowRevenueForm(false);
      setRevData({ amount: '', type: '', date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      console.error(err);
      alert('Failed to add revenue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expData.amount || !expData.description) return;
    
    setIsLoading(true);
    try {
      const added = await addExpense({
        amount: parseFloat(expData.amount),
        description: expData.description,
        date: new Date(expData.date || new Date()).toISOString()
      });
      setExpenses([added, ...expenses]);
      setShowExpenseForm(false);
      setExpData({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      console.error(err);
      alert('Failed to add expense');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Dashboard Overview</h1>
          <p className={styles.subtitle}>Welcome back! Here's what's happening today.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className={`${styles.addRevenueBtn} ${styles.expenseBtn}`} 
            onClick={() => { setShowExpenseForm(!showExpenseForm); setShowRevenueForm(false); }}
          >
            {showExpenseForm ? 'Cancel' : '+ Add Expense'}
          </button>
          <button 
            className={styles.addRevenueBtn} 
            onClick={() => { setShowRevenueForm(!showRevenueForm); setShowExpenseForm(false); }}
          >
            {showRevenueForm ? 'Cancel' : '+ Add Other Revenue'}
          </button>
        </div>
      </div>

      {showRevenueForm && (
        <div className={styles.revenueFormWrapper}>
          <form className={styles.revenueForm} onSubmit={handleAddOtherRevenue}>
            <div className={styles.formGroup}>
              <label>Amount (₹)</label>
              <input 
                type="number" 
                value={revData.amount} 
                onChange={e => setRevData({...revData, amount: e.target.value})} 
                required 
              />
            </div>
            <div className={styles.formGroup}>
              <label>Revenue Type / Description</label>
              <input 
                type="text" 
                value={revData.type} 
                onChange={e => setRevData({...revData, type: e.target.value})} 
                required 
                placeholder="e.g., Sponsorship, AdSense"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Date Received</label>
              <input 
                type="date" 
                value={revData.date} 
                onChange={e => setRevData({...revData, date: e.target.value})} 
                required 
              />
            </div>
            <button type="submit" className={styles.submitBtn}>Save Revenue</button>
          </form>
        </div>
      )}

      {showExpenseForm && (
        <div className={`${styles.revenueFormWrapper} ${styles.expenseFormWrapper}`}>
          <form className={styles.revenueForm} onSubmit={handleAddExpense}>
            <div className={styles.formGroup}>
              <label>Expense Amount (₹)</label>
              <input 
                type="number" 
                value={expData.amount} 
                onChange={e => setExpData({...expData, amount: e.target.value})} 
                required 
              />
            </div>
            <div className={styles.formGroup}>
              <label>Expense Description</label>
              <input 
                type="text" 
                value={expData.description} 
                onChange={e => setExpData({...expData, description: e.target.value})} 
                required 
                placeholder="e.g., Software Subscriptions, Marketing"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Expense Date</label>
              <input 
                type="date" 
                value={expData.date} 
                onChange={e => setExpData({...expData, date: e.target.value})} 
                required 
              />
            </div>
            <button type="submit" className={`${styles.submitBtn} ${styles.submitExpenseBtn}`}>Save Expense</button>
          </form>
        </div>
      )}

      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          Loading dashboard data...
        </div>
      ) : (
        <>
          <DashboardCards 
            clients={clients} 
            transactions={transactions} 
            otherRevenues={otherRevenues} 
            expenses={expenses} 
            onExpenseClick={() => setShowExpenseModal(true)} 
          />
          
          <UpcomingCollections clients={clients} />

          {otherRevenues.length > 0 && (
            <div className={styles.recentRevenuesWrapper}>
              <h3 className={styles.sectionTitle}>Other Revenue (Date-wise)</h3>
              <div className={styles.dateGroupList}>
                {sortedDates.map(dateStr => (
                  <div key={dateStr} className={styles.dateGroup}>
                    <h4 className={styles.dateHeader}>{dateStr}</h4>
                    <div className={styles.recentRevenuesList}>
                      {groupedOtherRevenues[dateStr].map(rev => (
                        <div key={rev.id} className={styles.recentRevenueItem}>
                          <span className={styles.revType}>{rev.type}</span>
                          <span className={styles.revAmount}>+ ₹{(parseFloat(rev.amount) || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showExpenseModal && (
            <div className={styles.modalOverlay} onClick={() => setShowExpenseModal(false)}>
              <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <h2 className={styles.modalTitle}>Monthly Expenses Breakdown</h2>
                  <button className={styles.closeBtn} onClick={() => setShowExpenseModal(false)}>&times;</button>
                </div>
                <div className={styles.modalBody}>
                  {sortedExpenseDates.length === 0 ? (
                    <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No expenses recorded yet.</p>
                  ) : (
                    <div className={styles.dateGroupList}>
                      {sortedExpenseDates.map(dateStr => (
                        <div key={dateStr} className={styles.dateGroup}>
                          <h4 className={styles.dateHeader}>{dateStr}</h4>
                          <div className={styles.recentRevenuesList}>
                            {groupedExpenses[dateStr].map(exp => (
                              <div key={exp.id} className={styles.recentRevenueItem}>
                                <span className={styles.revType}>{exp.description}</span>
                                <span className={styles.revAmount} style={{ color: '#ef4444' }}>- ₹{(parseFloat(exp.amount) || 0).toLocaleString('en-IN')}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={styles.chartSection}>
            <DashboardChart clients={clients} transactions={transactions} otherRevenues={otherRevenues} />
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
